import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Select } from "components/ui/select";

// Define a fade-in animation
const fadeInSlide = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animated components with sequential delays
const AnimatedQuestion = styled.div`
  opacity: 0;
  animation: ${fadeInSlide} 0.5s ease-out forwards;
  animation-delay: 0.1s;
  font-size: 18px;
  font-weight: 600;
  color: #2A3039;
  margin-bottom: 10px;
`;

const AnimatedDescription = styled.div`
  opacity: 0;
  animation: ${fadeInSlide} 0.5s ease-out forwards;
  animation-delay: 0.2s;
  font-weight: 300;
  font-size: 14px;
  margin-bottom: 20px;
  color: #4A5568;
`;

const AnimatedDateContainer = styled.div`
  opacity: 0;
  animation: ${fadeInSlide} 0.5s ease-out forwards;
  animation-delay: 0.3s;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
`;

const SelectContainer = styled.div`
  flex: 1;
  min-width: 120px;
`;

const SelectLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 5px;
  color: #4A5568;
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 10px;
  font-size: 1rem;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: white;
  outline: none;
  transition: border-color 0.2s;
  
  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const Qualifier = styled.div`
  opacity: 0;
  animation: ${fadeInSlide} 0.5s ease-out forwards;
  animation-delay: 0.1s;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
`;

export default function QuestionDate({ question, currentAnswer, onAnswerChange }) {
  // Generate arrays for days, months, and years
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];
  
  // Generate years (from current year down to 100 years ago)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  // Get current values from the answer object, or set defaults
  const day = currentAnswer?.day || "";
  const month = currentAnswer?.month || "";
  const year = currentAnswer?.year || "";

  // Function to update the date values
  const handleDateChange = (field, value) => {
    // Create new values object with the updated field
    const newDay = field === 'day' ? value : day;
    const newMonth = field === 'month' ? value : month;
    const newYear = field === 'year' ? value : year;
    
    // Create a formatted date only if all fields have values
    let formattedDate = '';
    if (newDay && newMonth && newYear) {
      // Format: DD/MM/YYYY
      formattedDate = `${newDay.toString().padStart(2, '0')}/${newMonth.toString().padStart(2, '0')}/${newYear}`;
    }
    
    const updatedAnswer = {
      ...currentAnswer,
      day: newDay,
      month: newMonth,
      year: newYear,
      formattedDate,
      // Also store a JS Date object for easier comparison in conditional logic
      dateObject: newDay && newMonth && newYear ? new Date(newYear, newMonth - 1, newDay) : null
    };
    
    onAnswerChange(question.uniqueId, updatedAnswer);
  };

  return (
    <div style={{ marginTop: "1rem" }}>
      {question.qualifier && <Qualifier>{question.qualifier}</Qualifier>}
      <AnimatedQuestion>{question.question}</AnimatedQuestion>
      
      {question.description && (
        <AnimatedDescription>{question.description}</AnimatedDescription>
      )}
      
      <AnimatedDateContainer>
        <SelectContainer>
          <SelectLabel>Day</SelectLabel>
          <StyledSelect 
            value={day} 
            onChange={(e) => handleDateChange('day', e.target.value)}
          >
            <option value="">Select Day</option>
            {days.map(d => (
              <option key={`day-${d}`} value={d}>{d}</option>
            ))}
          </StyledSelect>
        </SelectContainer>
        
        <SelectContainer>
          <SelectLabel>Month</SelectLabel>
          <StyledSelect 
            value={month} 
            onChange={(e) => handleDateChange('month', e.target.value)}
          >
            <option value="">Select Month</option>
            {months.map(m => (
              <option key={`month-${m.value}`} value={m.value}>{m.label}</option>
            ))}
          </StyledSelect>
        </SelectContainer>
        
        <SelectContainer>
          <SelectLabel>Year</SelectLabel>
          <StyledSelect 
            value={year} 
            onChange={(e) => handleDateChange('year', e.target.value)}
          >
            <option value="">Select Year</option>
            {years.map(y => (
              <option key={`year-${y}`} value={y}>{y}</option>
            ))}
          </StyledSelect>
        </SelectContainer>
      </AnimatedDateContainer>
    </div>
  );
} 